import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import type { LucideIcon } from "lucide-react";
import {
  Check,
  ChevronRight,
  CircleSlash,
  Clock,
  FolderUp,
  Mail,
  Plus,
  RotateCw,
  Send,
  X,
} from "lucide-react";
import { useState } from "react";

import type { API } from "@upclaim/sdk";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { initialsOf } from "@/lib/initials";
import { isAdult } from "@/lib/is-adult";
import { cn } from "@/lib/utils";

import { useClaim } from "../../api/use-claim";
import {
  useCancelPassengerRequest,
  useCreatePassengerRequests,
  useNotifyPassengerRequests,
  usePassengerRequests,
} from "../../api/use-passenger-requests";
import { type AmountWithConversions, formatAmountWithConversion } from "../../lib/money";
import type { FamilyClaimAcquisition } from "../acquisition/acquisition-steps";
import { getAcquisitionPassengerName } from "../acquisition/acquisition-steps";
import { CreatePassengerRequestsDialog } from "./create-passenger-requests-dialog";
import { relanceSchedule } from "./relance-schedule";
import { requestTypeVisual, requestVisualKey } from "./request-type-visual";

/**
 * `Badge` ships its variants with a transparent border, so the tint is set per status here —
 * the outline is what separates a status pill from the row's own background at a glance.
 *
 * The icon matters more than the colour: a jurist scanning a column of requests reads the shape
 * before the hue, and colour alone is not a signal everyone receives.
 */
const STATUS_BADGE: Record<
  API.PassengerRequest["status"],
  {
    label: string;
    variant: "secondary" | "success" | "outline";
    className?: string;
    Icon?: LucideIcon;
  }
> = {
  PENDING: { label: "En attente", variant: "secondary", className: "border-border", Icon: Clock },
  COMPLETED: { label: "Reçue", variant: "success", className: "border-success/24", Icon: Check },
  REFUSED: { label: "Refusée", variant: "outline", Icon: CircleSlash },
  CANCELLED: { label: "Annulée", variant: "outline" },
};

/**
 * Who advanced a claim-level expense. The enum is `claim_expense_paid_by`, but the unified
 * `claimExpenses` array also carries passenger rows whose `paidBy` is a person's name — those are
 * filtered out before we get here, and the lookup falls back to the raw value either way.
 */
const PAID_BY_LABELS: Record<string, string> = {
  TRAVEL_AGENCY: "Agence de voyage",
  BUSINESS_TRAVEL_COMPANY: "Entreprise",
};

function formatDate(iso: string) {
  return format(new Date(iso), "dd MMM yyyy", { locale: fr });
}

/** Short form for the relance calendar, where the year is never in question. */
function formatShortDate(date: Date) {
  return format(date, "d MMM", { locale: fr });
}

/** Requests nobody declared: the dossier's own expenses, advanced by the agency or the company. */
const DOSSIER_GROUP = "Dossier";

/**
 * A pièce belongs to somebody, so the name leads and its requests hang under it.
 *
 * A flat list made the jurist read every row to answer "what is Jean still missing", which is the
 * question they actually arrive with. Insertion order is kept, so the groups follow the order the
 * requests were created in.
 */
function groupByTarget(requests: API.PassengerRequest[]) {
  const groups = new Map<string, API.PassengerRequest[]>();

  for (const request of requests) {
    const key = request.targetName ?? DOSSIER_GROUP;
    const existing = groups.get(key);

    if (existing) {
      existing.push(request);
    } else {
      groups.set(key, [request]);
    }
  }

  return [...groups].map(([name, items]) => ({ name, requests: items }));
}

/** What the group header says after the name — the counts, not a repetition of the rows. */
function groupSummary(requests: API.PassengerRequest[]) {
  const received = requests.filter((request) => request.status === "COMPLETED").length;
  const pending = requests.filter((request) => request.status === "PENDING").length;

  return [
    received > 0 ? `${received} reçue${received > 1 ? "s" : ""}` : null,
    pending > 0 ? `${pending} en attente` : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

function StatusBadge({ status }: { status: API.PassengerRequest["status"] }) {
  const { label, variant, className, Icon } = STATUS_BADGE[status];

  return (
    <Badge variant={variant} className={cn("mt-1", className)}>
      {Icon ? <Icon /> : null}
      {label}
    </Badge>
  );
}

/** The kind of ask at a glance — the same icon and colour as in « Demander des pièces ». */
function RequestTypeIcon({ request }: { request: API.PassengerRequest }) {
  const { icon: Icon, className } = requestTypeVisual(requestVisualKey(request));
  return (
    <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-md bg-muted">
      <Icon aria-hidden="true" className={cn("size-4", className)} />
    </span>
  );
}

/** Who asked and how long ago, with the jurist's face: the full date stays a hover away. */
function RequesterLine({ request }: { request: API.PassengerRequest }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={formatDate(request.createdAt)}>
      <Avatar className="size-4 shrink-0 bg-gray-100 text-[8px] text-gray-600">
        {request.requestedByPictureUrl ? (
          <AvatarImage src={request.requestedByPictureUrl} alt={request.requestedByName} />
        ) : null}
        <AvatarFallback>{initialsOf(request.requestedByName)}</AvatarFallback>
      </Avatar>
      <span className="truncate">
        {request.requestedByName} · il y a {formatDistanceToNow(new Date(request.createdAt), { locale: fr })}
        {request.isRequired ? null : " · sans relance"}
      </span>
    </span>
  );
}

/** What the row's second line says depends on how it ended. */
function subtitle(request: API.PassengerRequest) {
  if (request.status === "COMPLETED") {
    return `Reçue le ${formatDate(request.completedAt ?? request.createdAt)}${
      request.documentFileName ? ` · ${request.documentFileName}` : ""
    }`;
  }
  if (request.status === "REFUSED") {
    return `Déclinée par le passager · ${formatDate(request.refusedAt ?? request.createdAt)}`;
  }
  if (request.status === "CANCELLED") {
    return `Annulée · demandée le ${formatDate(request.createdAt)}`;
  }
  return `Demandée par ${request.requestedByName} · ${formatDate(request.createdAt)}${
    request.isRequired ? "" : " — ne déclenche pas de relance"
  }`;
}

export function PassengerRequestsSection({
  claimId,
  acquisition,
  className,
}: {
  claimId: string;
  acquisition: FamilyClaimAcquisition;
  className?: string;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // `null` tant que l'admin n'a pas plié / déplié : l'état des demandes décide alors.
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);

  const { data: allRequests } = usePassengerRequests(claimId);
  const { data: claim } = useClaim(claimId);
  const create = useCreatePassengerRequests(claimId);
  const notify = useNotifyPassengerRequests(claimId);
  const cancel = useCancelPassengerRequest(claimId);

  const requests = (allRequests ?? []).filter(
    (request) => request.familyClaimAcquisitionId === acquisition.id,
  );

  const pendingRequired = requests.filter(
    (request) => request.isRequired && request.status === "PENDING",
  ).length;
  const hasPending = requests.some((request) => request.status === "PENDING");
  const pendingCount = requests.filter((request) => request.status === "PENDING").length;
  // Ouvert quand le passager a encore quelque chose à fournir ; plié sinon (historique seulement).
  const isOpen = openOverride ?? hasPending;
  // The same endpoint serves both; only `notifiedAt` tells the jurist which one they are doing.
  const alreadySent = requests.some((request) => request.notifiedAt);
  const lastSentAt = requests
    .map((request) => request.notifiedAt)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);

  const passengers = acquisition.family.usersClaims.map((userClaim) => ({
    id: userClaim.id,
    name: getAcquisitionPassengerName(userClaim.user),
    email: userClaim.email ?? null,
    expenses: (userClaim.expenses ?? []).map(({ expense }) => ({
      id: expense.id,
      label: [
        expense.expenseName ?? expense.expenseType?.type ?? "Dépense",
        // `price` is raw minor units with no precision or currency — it renders "3000" for 30 €.
        expense.amount ? formatAmountWithConversion(expense.amount as AmountWithConversions) : null,
      ]
        .filter(Boolean)
        .join(" · "),
    })),
  }));

  /**
   * Expenses the dossier carries rather than a passenger: advanced by the travel agency or the
   * business travel company, so nobody who flew declared them and no passenger can be named.
   *
   * `claim.claimExpenses` is a **unified** array — `retrieve-claim-with-all-relations` appends
   * every `UserClaimExpense` onto it for the <Expenses /> panel — so the entries carrying a
   * `userClaimId` are passenger ones already offered above. Filtering them out is what leaves the
   * real `claims_expenses` rows (TECH-1035).
   */
  const claimExpenses = (claim?.claimExpenses ?? [])
    .filter((claimExpense) => !claimExpense.userClaimId)
    .map(({ expense, paidBy }) => ({
      id: expense.id,
      label: [
        expense.expenseName ?? expense.expenseType?.type ?? "Dépense",
        expense.amount ? formatAmountWithConversion(expense.amount as AmountWithConversions) : null,
        // What tells these apart from a passenger's own: who actually paid.
        PAID_BY_LABELS[paidBy] ?? paidBy,
      ]
        .filter(Boolean)
        .join(" · "),
    }));

  /**
   * Who we can ask an address for: every passenger of the CLAIM, not just this family's.
   *
   * A passenger whose address is missing belongs, by construction, to a family nobody has ever
   * written to — which means that family has no acquisition, so it never appears in this
   * family-scoped view. Sourcing the list from `acquisition.family` left the picker permanently
   * empty for exactly the people it exists to reach. The API has always accepted any userClaim of
   * the claim here (`create-passenger-requests` resolves against `claimId`).
   *
   * An address already on file is no longer a reason to exclude somebody: it is regularly the
   * wrong one (somebody gave their own when asked for a relative's), and only the jurist looking
   * at the file knows. The address is carried along so the picker can show what is already there.
   *
   * Adults only: a minor cannot be written to and cannot sign, so their address buys us nothing —
   * it is their legal representative we would need instead. An unknown birthdate counts as adult.
   */
  const emailTargets = (claim?.usersClaims ?? [])
    .filter((userClaim) => !userClaim.isRemoved && isAdult(userClaim.user?.birthdate))
    .map((userClaim) => ({
      id: userClaim.id,
      name: getAcquisitionPassengerName(userClaim.user),
      email: userClaim.email ?? null,
      expenses: [],
    }));

  /**
   * Families a legal representative can be asked for: every passenger is a minor, and none is on file yet.
   *
   * Represented by one of their minors, because a `PassengerRequest` cannot point at a family —
   * the API reads the family off that UserClaim. A family with an adult is excluded: somebody
   * there can sign, so a legal representative would buy us nothing.
   */
  const legalRepresentativeTargets = (claim?.usersClaimsFamilies ?? [])
    .filter((family) => !family.legalRepresentative)
    .map((family) => {
      const members = (claim?.usersClaims ?? []).filter(
        (userClaim) => userClaim.familyId === family.id && !userClaim.isRemoved,
      );

      return {
        family,
        minors: members.filter((userClaim) => !isAdult(userClaim.user?.birthdate)),
        hasAdult: members.some((userClaim) => isAdult(userClaim.user?.birthdate)),
      };
    })
    .filter(({ minors, hasAdult }) => !hasAdult && minors.length > 0)
    .map(({ family, minors }) => ({
      id: minors[0].id,
      name: `Famille ${family.familyName}`,
      email: null,
      expenses: [],
    }));

  // The legal representative hangs off the family, not the acquisition (TECH-968).
  const representative = acquisition.family.legalRepresentative;
  const legalRepresentative = representative
    ? { id: representative.id, name: `${representative.firstName} ${representative.lastName}` }
    : null;

  return (
    <Collapsible open={isOpen} onOpenChange={setOpenOverride} className={cn("mt-6", className)}>
      <div className="mb-3 flex items-start gap-2">
        {requests.length === 0 ? (
          <span className="flex min-w-0 flex-col gap-0.5 text-sm">
            <span className="flex items-center gap-2 whitespace-nowrap font-semibold text-foreground">
              <FolderUp className="size-4 shrink-0 text-muted-foreground" />
              Demandes passager
            </span>
            <span className="ml-6 text-xs text-muted-foreground">Aucune pièce demandée à cette famille</span>
          </span>
        ) : (
          <CollapsibleTrigger className="group flex min-w-0 flex-col items-start gap-0.5 text-left text-sm">
            <span className="flex items-center gap-2 whitespace-nowrap font-semibold text-foreground">
              <ChevronRight
                aria-hidden="true"
                className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[panel-open]:rotate-90"
              />
              Demandes passager
            </span>
            {/* The counts under the title, never beside it: in a claim column they pushed the title
                onto two lines and truncated themselves. */}
            <span className="ml-6 text-xs text-muted-foreground">
              {requests.length} pièce{requests.length > 1 ? "s" : ""}
              {pendingRequired > 0 ? (
                <span className="text-warning-foreground">
                  {" "}
                  · {pendingRequired} obligatoire{pendingRequired > 1 ? "s" : ""} en attente
                </span>
              ) : pendingCount > 0 ? (
                ` · ${pendingCount} en attente`
              ) : (
                " · aucune en attente"
              )}
            </span>
          </CollapsibleTrigger>
        )}
        <Button className="ml-auto shrink-0" onClick={() => setDialogOpen(true)} size="sm" variant="outline">
          <Plus />
          Demander des pièces
        </Button>
      </div>

      {requests.length === 0 ? null : (
        <CollapsiblePanel keepMounted>
          {/* Same surface as the request cards below: the send bar is part of the list, not a callout. */}
          <Card className="mb-4 gap-2 px-3.5 py-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="flex items-center gap-1.5 text-sm text-foreground">
                <Mail className="size-4 shrink-0 text-muted-foreground" />
                {alreadySent && lastSentAt ? (
                  <>
                    Envoyée le <span className="whitespace-nowrap font-medium">{formatDate(lastSentAt)}</span>
                  </>
                ) : (
                  "Pas encore envoyée au passager"
                )}
              </span>
              <Button
                className="ml-auto shrink-0"
                variant="outline"
                size="sm"
                disabled={!hasPending || notify.isPending}
                onClick={() =>
                  notify.mutate({ familyClaimAcquisitionId: acquisition.id, isRelaunch: alreadySent })
                }
              >
                {alreadySent ? <RotateCw /> : <Send />}
                {alreadySent ? "Relancer" : "Envoyer la demande"}
              </Button>
            </div>

            {/* The calendar, not the policy: which relaunch leaves next, and on what day. The rule
                  itself is a hover away on the label. */}
            {lastSentAt && pendingRequired > 0 ? (
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground"
                title="Une relance part tant qu'une pièce obligatoire manque ; une relance manuelle remet le compteur à zéro."
              >
                <span>Relances</span>
                {relanceSchedule(lastSentAt).map((step) => (
                  <span
                    key={step.days}
                    className={cn(
                      "inline-flex items-center gap-1 tabular-nums",
                      step.next && "font-semibold text-primary",
                      step.done && "line-through decoration-muted-foreground/50",
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-1.5 rounded-full",
                        step.next ? "bg-primary" : step.done ? "bg-muted-foreground" : "border border-border",
                      )}
                    />
                    <span>J+{step.days}</span>
                    <span>{formatShortDate(step.at)}</span>
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">
                Aucune relance : aucune pièce obligatoire n&apos;est en attente.
              </span>
            )}
          </Card>

          <div className="flex flex-col gap-4">
            {groupByTarget(requests).map((group) => (
              <div key={group.name} className="flex flex-col gap-1.5">
                <div className="flex items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-foreground">{group.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {/* A dossier-wide question lands here too, and it is no agency expense. */}
                    {group.name === DOSSIER_GROUP &&
                    !group.requests.some((request) => request.type === "QUESTION")
                      ? "frais avancés par l'agence — aucun passager désigné"
                      : groupSummary(group.requests)}
                  </span>
                </div>

                <Card className="overflow-hidden">
                  {group.requests.map((request, index) => (
                    <div
                      key={request.id}
                      className={`flex items-start gap-3 px-3.5 py-3 ${index > 0 ? "border-t" : ""}`}
                    >
                      <RequestTypeIcon request={request} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={
                              request.status === "CANCELLED" || request.status === "REFUSED"
                                ? "font-medium text-muted-foreground line-through"
                                : "font-medium text-foreground"
                            }
                          >
                            {request.title}
                          </span>
                          {/* The target moved to the group header — repeating it on every row is noise. */}
                          <Badge
                            variant={request.isRequired ? "warning" : "outline"}
                            className={request.isRequired ? "border-warning/24" : undefined}
                          >
                            {request.isRequired ? "Obligatoire" : "Si disponible"}
                          </Badge>
                        </div>
                        {request.type === "QUESTION" && request.description ? (
                          <p className="mt-1 text-sm text-foreground">{request.description}</p>
                        ) : null}
                        <div className="mt-0.5 flex text-xs text-muted-foreground">
                          {request.status === "PENDING" ? (
                            <RequesterLine request={request} />
                          ) : (
                            subtitle(request)
                          )}
                        </div>
                        {/* The answer is the point of the row: shown whole, line breaks kept. */}
                        {request.type === "QUESTION" && request.passengerAnswer ? (
                          <div className="mt-2 rounded-md border bg-muted/50 px-3 py-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Réponse du passager
                            </span>
                            <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">
                              {request.passengerAnswer}
                            </p>
                          </div>
                        ) : null}
                      </div>

                      <StatusBadge status={request.status} />

                      {request.status === "PENDING" ? (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={`Annuler la demande « ${request.title} » de ${group.name}`}
                          disabled={cancel.isPending}
                          onClick={() => cancel.mutate(request.id)}
                        >
                          <X />
                        </Button>
                      ) : (
                        <span className="inline-block w-6" />
                      )}
                    </div>
                  ))}
                </Card>
              </div>
            ))}
          </div>
        </CollapsiblePanel>
      )}

      {dialogOpen ? (
        <CreatePassengerRequestsDialog
          open
          onOpenChange={setDialogOpen}
          passengers={passengers}
          emailTargets={emailTargets}
          legalRepresentativeTargets={legalRepresentativeTargets}
          legalRepresentative={legalRepresentative}
          claimExpenses={claimExpenses}
          pending={create.isPending}
          onSubmit={(requestsPayload) =>
            create.mutate(
              { familyClaimAcquisitionId: acquisition.id, requests: requestsPayload },
              { onSuccess: () => setDialogOpen(false) },
            )
          }
        />
      ) : null}
    </Collapsible>
  );
}
